import React, { useMemo, useState } from "react";
import {
  Accordion,
  Button,
  Col,
  Container,
  Dropdown,
  Form,
  Row,
} from "react-bootstrap";
import Switch from "react-switch";

import "react-phone-input-2/lib/style.css";
// css
import styles from "./TherapistDetail.module.scss";
import { generatePath, Link, useNavigate, useParams } from "react-router-dom";

// img
import p1 from "../../../../../Assets/images/user.png";
import logo from "../../../../../Assets/images/logo.png";
import formIcn from "../../../../../Assets/images/formIcn.svg";
import AssignDisciplinePop from "../../../../../components/Modals/AssignDisciplinePop";
import TableLayout from "../../../../../components/TableLayout";
import TableLayoutV2New from "../../../../../components/TableLayoutV2New";
import {
  Breadcrumbs,
  CommonSelect,
  CstmPagination,
  NoRecordFoundV2,
} from "../../../../../components/Common/Common";
import { useGetTherapistDataQuery, useTherapistAsCollaboratorMutation } from "store/services/therapistProfiles";
import { useGetTherapistPoliciesListQuery } from "store/services/policies";
import { dateFormat, getNameInitials, parseHtmlHelper, userFullName } from "helpers/common";
import moment from "moment";
import ErrorComponent from "components/shared/ErrorComponent";
import LoadingIndicator from "components/shared/LoadingIndicator";
import ClipboardCopyBtn from "components/shared/ClipboardCopyBtn";
import { CLIENT_TYPE, CLIENT_TYPE_OPTIONS } from "config/constants";
import confirmDialogV2 from "utils/confirmationDialogV2";
import LoadingOverlay from "components/shared/LoadingOverlay";
import { THERAPIST_ASSIGNED_CLIENTS, THERAPIST_EDIT, THERAPIST_LIST } from "config/paths";
import { useDisciplineLookupQuery } from "store/services/disciplines";
import { useLazyGetFileUrlQuery } from "store/services/utility";
import PolicyPreviewModal from "components/Modals/PolicyPreviewModal";

const ProfileDetailField = ({ label, children, className = "" }) => (
  <div className={`${styles.detailField} ${className}`}>
    <h4>{label}</h4>
    <div className={styles.detailValue}>{children || "--"}</div>
  </div>
);

const DocumentLink = ({ url }) => (
  <a href={url} style={{ textDecoration: "underline", color: "black" }} target="_blank" rel="noreferrer">
    View Document
  </a>
);

const TherapistDetailV2 = ({ therapist, disciplines = [], onEdit, onCollaboratorChange, collaboratorDisabled, policies = [], policyFilter, onPolicySort, policiesLoading, policiesError, policiesFetching }) => {
  const { id: therapistId } = useParams();
  const details = therapist?.therapist_detail || {};
  const therapistName = userFullName(therapist) || "Clinician";
  const emergencyName = [details.emergency_firstname, details.emergency_lastname].filter(Boolean).join(" ");
  const address = [details.address1, details.address2, [details.city, details.postal_code].filter(Boolean).join(", ")].filter(Boolean);
  const selectedDisciplineId = details?.discipline_id || therapist?.discipline_id || therapist?.therapist_discipline?.id || details?.therapist_discipline?.id;
  const designation = therapist?.therapist_designation?.title || details?.therapist_designation?.title;
  const assignedClientCategories = (therapist?.assigned_client_category || details?.assigned_client_category || "").split(",").filter(Boolean);
  const therapistDocuments = therapist?.therapistDocuments || {};
  const professionalLiabilityDocuments = therapistDocuments?.professional_liabilities_insurances || [];
  const [getFileUrl, { isFetching: isPolicyDocumentFetching }] = useLazyGetFileUrlQuery();
  const [policyDocumentUrl, setPolicyDocumentUrl] = useState("");
  const documents = [
    { id: "policy-check", name: "Policy Check", url: therapistDocuments.policy_check, createdAt: therapistDocuments.policy_check_uploaded_at },
    { id: "business-license", name: "Business License", url: therapistDocuments.business_license, createdAt: therapistDocuments.business_license_uploaded_at },
    { id: "college-registration", name: "College Registration", url: therapistDocuments.college_registration, createdAt: therapistDocuments.college_registration_uploaded_at },
    {
      id: "professional-liability-insurance",
      name: "Professional Liability Insurance",
      url: therapistDocuments.professional_liability_insurance || therapistDocuments.professional_liabilities_insurance,
      createdAt: therapistDocuments.professional_liability_insurance_uploaded_at,
    },
    ...professionalLiabilityDocuments.map((document, index) => ({
      id: document.id || `professional-liability-${index}`,
      name: document.label || "Professional Liability Insurance",
      url: document.file,
      createdAt: document?.createdAt,
    })),
  ].filter((document) => document.url);

  const handleViewPolicyDocument = async (attachmentKey) => {
    if (!attachmentKey) return;

    try {
      const result = await getFileUrl(attachmentKey);
      if (result?.error || !result?.data) return;

      setPolicyDocumentUrl(result.data);
    } catch (error) {
      console.error("Failed to retrieve policy document: ", error);
    }
  };

  const policyColumns = [
    {
      head: "POLICY NAME",
      accessor: "name",
      sorter: true,
      component: (item) => item?.name || item?.policy?.name || item?.title || "--",
    },
    {
      head: "DOCUMENT",
      accessor: "attachment_name",
      component: (item) => {
        const attachmentKey = item?.attachment_key || item?.policy?.attachment_key || item?.attachment?.attachment_key;
        const attachmentName = item?.attachment_name || item?.policy?.attachment_name || item?.attachment?.attachment_name;

        return attachmentKey ? (
          <button
            type="button"
            className={styles.viewDocumentButton}
            onClick={(event) => {
              event.stopPropagation();
              handleViewPolicyDocument(attachmentKey);
            }}
          >
            {attachmentName || "View Document"}
          </button>
        ) : "--";
      },
    },
    {
      head: "DATE OF AGREEMENT",
      accessor: "agreed_at",
      sorter: true,
      component: (item) => item?.agreed_at ? moment(item.agreed_at).format("DD/MM/YYYY") : "Pending",
    },
  ];

  const documentColumns = [
    {
      head: "DOCUMENT NAME",
      accessor: "name",
      component: (item) => item.name || "--",
    },
    {
      // head: "DOCUMENT",
      accessor: "url",
      component: (item) => <DocumentLink url={item.url} />,
    },
    {
      head: "CREATED AT",
      accessor: "createdAt",
      component: (item) => item.createdAt ? dateFormat(item.createdAt, "MMM DD, YYYY") : "--",
    },
  ];

  const breadcrumbItems = [
    { label: "Clinicians", path: THERAPIST_LIST },
    { label: therapistName, path: generatePath(THERAPIST_ASSIGNED_CLIENTS, { id: therapistId }) },
    { label: "Profile Details" },
  ];

  return (
    <section className={styles.therapistDetailsV2}>
      <PolicyPreviewModal
        documentUrl={policyDocumentUrl}
        onHide={() => setPolicyDocumentUrl("")}
      />

      <div className=" mb-4">
        <Breadcrumbs themeBlueV2 items={breadcrumbItems} />
      </div>

      <h2 className={styles.sectionTitle}>Clinician Details</h2>
      <div className={styles.sectionDivider} />

      <div className={styles.profileGrid}>
        <div className={styles.profileImageWrap}>
          {details.profile_picture ? (
            <img src={details.profile_picture} alt={`${therapistName}'s profile`} className={styles.profileImage} />
          ) : (
            <div className={styles.profilePlaceholder} aria-label="Profile image unavailable">
              <span />
            </div>
          )}
        </div>
        <div className={styles.profileContent}>
          <h1 className={styles.therapistName}>{therapistName}</h1>
          <div className={styles.detailColumns}>
            <div>
              <ProfileDetailField label="Address">
                {address.length ? address.map((line) => <div key={line}>{line}</div>) : "--"}
              </ProfileDetailField>
              <ProfileDetailField label="Timezone">{details.timezone}</ProfileDetailField>
                <ProfileDetailField label="Created At">{dateFormat(therapist?.createdAt, "MMM DD, YYYY hh:mm A")}</ProfileDetailField>
              <ProfileDetailField label="Signature Image">
                <div className={`${styles.signatureBox} ${details.signature_img ? "" : styles.signatureEmpty}`}>
                  {details.signature_img && <img src={details.signature_img} alt={`${therapistName}'s signature`} />}
                </div>
              </ProfileDetailField>
            </div>
            <div>
              <ProfileDetailField label="Email Address">
                {therapist?.email ? <a href={`mailto:${therapist.email}`}>{therapist.email}</a> : "--"}
              </ProfileDetailField>
              <ProfileDetailField label="Phone Number">{therapist?.phone}</ProfileDetailField>
            </div>
          </div>
        </div>
      </div>

      <h2 className={`${styles.sectionTitle} ${styles.emergencyTitle}`}>Emergency Contact Information</h2>
      <div className={styles.sectionDivider} />
      <div className={`${styles.detailColumns} ${styles.emergencyColumns}`}>
        <div>
          <ProfileDetailField label="Name">{emergencyName}</ProfileDetailField>
          <ProfileDetailField label="Relationship to clinician">{details.emergency_relationship}</ProfileDetailField>
        </div>
        <div>
          <ProfileDetailField label="Email Address">
            {details.emergency_email ? <a href={`mailto:${details.emergency_email}`}>{details.emergency_email}</a> : "--"}
          </ProfileDetailField>
          <ProfileDetailField label="Phone Number">{details.emergency_phone}</ProfileDetailField>
        </div>
      </div>

      <section className={styles.documentsSection}>
        <h2 className={styles.sectionTitle}>Documents</h2>
        <div className={styles.sectionDivider} />
        <div className={`${styles.documentsTable} position-relative`}>
          <TableLayoutV2New
            data={documents}
            column={documentColumns}
            height="auto"
            noDataMsg="No documents available"
          />
        </div>
      </section>

      <section className={styles.kcsInformation}>
        <div className={styles.kcsHeader}>
          <h2 className={styles.sectionTitle}>KCS Information</h2>
          <button type="button" className={styles.editButton} onClick={onEdit}>Edit</button>
        </div>
        <div className={styles.sectionDivider} />
        <div className={styles.kcsGrid}>
          <ProfileDetailField label="Discipline*">
            <div className={styles.disciplineTags}>
              {disciplines.map((discipline) => (
                <span
                  key={discipline.id}
                  className={`${styles.detailTag} ${Number(discipline.id) === Number(selectedDisciplineId) ? styles.selectedDetailTag : ""}`}
                >
                  {discipline.assign_term}
                </span>
              ))}
            </div>
          </ProfileDetailField>
          <ProfileDetailField label="Designation">{designation}</ProfileDetailField>
          <ProfileDetailField label="Assigned Discipline Type">{therapist?.therapist_discipline?.name}</ProfileDetailField>
          <ProfileDetailField label="Type">{therapist?.type}</ProfileDetailField>
          <div className={styles.clientCategory}>
            <h4>Client Category*</h4>
            <div className={styles.categoryList}>
              {CLIENT_TYPE_OPTIONS.map((category) => (
                <label key={category.value}>
                  <input type="checkbox" checked={assignedClientCategories.includes(category.value)} readOnly />
                  <span>{category.label}</span>
                </label>
              ))}
            </div>
          </div>
          <div className={styles.collaboratorOption}>
            <h4>Collaborator</h4>
            <label>
              <input
                type="checkbox"
                checked={Boolean(therapist?.colloborators_on_notes)}
                disabled={collaboratorDisabled}
                onChange={onCollaboratorChange}
              />
              <span>Enable collaborator access</span>
            </label>
          </div>
        </div>
        <div className={styles.policiesSection}>
          <h3 className={styles.sectionTitle}>Policies and Procedures</h3>
          <div className={styles.sectionDivider} />
          <div className={`${styles.policiesTable} position-relative`}>
            {policiesLoading ? <div className={styles.policiesLoading}><LoadingIndicator /></div> : policiesError ? <ErrorComponent /> : <>
              {(policiesFetching || isPolicyDocumentFetching) && <LoadingOverlay />}
              <TableLayoutV2New
                data={policies}
                column={policyColumns}
                filter={policyFilter}
                handleSort={onPolicySort}
                height="auto"
                noDataMsg="No policies assigned"
              />
            </>}
          </div>
        </div>
      </section>

    </section>
  );
};

const TherapistDetail = () => {
  const { id: therapistId } = useParams();
  const navigate = useNavigate();
  const {
    data: therapistData,
    isLoading: getTherapistLoading,
    isError: getTherapistError,
    isFetching
  } = useGetTherapistDataQuery(therapistId, { skip: !therapistId });
  const { data: disciplinesList = [] } = useDisciplineLookupQuery({});

  const [updateCollaborator, { isLoading: updateCollaboratorLoading }] = useTherapistAsCollaboratorMutation();
  const [policyPage, setPolicyPage] = useState(1);
  const [policyFilter, setPolicyFilter] = useState({ sort: { column: "agreed_at", direction: "DESC" } });
  const { data: policyData, isLoading: policiesLoading, isError: policiesError, isFetching: policiesFetching } = useGetTherapistPoliciesListQuery({
    therapistId,
    page: policyPage,
    limit: 10,
    sort: policyFilter.sort,
  }, { skip: !therapistId });

  const policies = policyData?.records || policyData?.policies || policyData?.data || [];
  const policyTotalPages = Math.max(1, Math.ceil((policyData?.totalRecords || policyData?.total_records || policyData?.count || 0) / 10));

  const handlePolicySort = (sort) => {
    setPolicyFilter((current) => ({ ...current, sort }));
    setPolicyPage(1);
  };

  const assignedSchools = useMemo(() => {
    if (!therapistData?.school_therapists?.length) {
      return [];
    }
    const map = {};

    therapistData?.school_therapists.forEach(item => {
      const divisionId = item.divisions.id;

      if (!map[divisionId]) {
        map[divisionId] = {
          divisionId,
          divisionTitle: item.divisions.title,
          schools: []
        };
      }

      map[divisionId].schools.push({
        id: item.schools.id,
        name: item.schools.name
      });
    });

    return Object.values(map);
  }, [therapistData?.school_therapists])

  const [checked, setChecked] = useState(false);
  const [permissions, setPermssion] = useState(false);
  const [assignDiscipline, setAssignDiscipline] = useState(false);

  const handleChange = (checked) => {
    setChecked(checked);
  };
  const handleDiscipline = () => setAssignDiscipline(!assignDiscipline);

  const serviceOption = [
    { value: "School", label: "School" },
    { value: "HomeEducation", label: "HomeEducation" },
    { value: "Chatter", label: "Chatter" },
    { value: "Private Client", label: "Private Client" },
  ];

  const handleUpdateCollaborator = async (e) => {
    const isConfirm = await confirmDialogV2({
      title: "THERAPIST AS COLLABORATOR ?",
      message: therapistData?.colloborators_on_notes ? "Are you sure you want to remove this therapist as a collaborator?" : "Are you sure you want to make this therapist a collaborator?"
    });
    if (!isConfirm) {
      return;
    }
    const data = {
      "colloborators_on_notes": therapistData?.colloborators_on_notes ? false : true
    }
    try {
      await updateCollaborator({ id: therapistId, data }).unwrap();
    } catch (error) {
      console.error("Failed to update collaborator status: ", error);
    }
  }

  return (
    <>
      <AssignDisciplinePop
        assignDiscipline={assignDiscipline}
        setAssignDiscipline={setAssignDiscipline}
      />
      <section
        className={`${styles.TherapistDetail} position-relative TherapistDetail`}
      >
        <Container fluid={true}>
          <Row>
            <Col lg="12">
              {getTherapistLoading ? (
                <div className="vh-100">
                  <LoadingIndicator />
                </div>
              ) : getTherapistError ? (
                <ErrorComponent />
              ) : (
                <Row>
                  {
                    (updateCollaboratorLoading || isFetching) && <LoadingOverlay />
                  }
                  <Col lg="12">
                    <TherapistDetailV2
                      therapist={therapistData}
                      disciplines={disciplinesList}
                      onEdit={() => navigate(THERAPIST_EDIT.replace(":id", therapistId))}
                      onCollaboratorChange={handleUpdateCollaborator}
                      collaboratorDisabled={isFetching || updateCollaboratorLoading}
                      policies={policies}
                      policyFilter={policyFilter}
                      onPolicySort={handlePolicySort}
                      policiesLoading={policiesLoading}
                      policiesError={policiesError}
                      policiesFetching={policiesFetching}
                    />
                    {!policiesLoading && !policiesError && <CstmPagination totalPages={policyTotalPages} activePage={policyPage} className="paginationV2" handlePageChange={setPolicyPage} />}
                  </Col>
                  {/* <Col lg="12">
                    <div className={styles.legacySeparator} />
                  </Col> */}
                  {permissions ? (
                    <>
                      {/* <Col lg="12" className="my-2">
                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-10 border-bottom py-3">
                          <div className="d-flex align-items-center gap-10">
                            <Button
                              onClick={() => setPermssion(!permissions)}
                              className="border-0 p-0"
                              variant="transparent"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="20"
                                height="14"
                                viewBox="0 0 20 14"
                                fill="none"
                              >
                                <path
                                  d="M6 12L0 6L6 0L7.425 1.4L2.825 6L7.425 10.6L6 12ZM18 14V10C18 9.16667 17.7083 8.45833 17.125 7.875C16.5417 7.29167 15.8333 7 15 7H8.825L12.425 10.6L11 12L5 6L11 0L12.425 1.4L8.825 5H15C16.3833 5 17.5625 5.4875 18.5375 6.4625C19.5125 7.4375 20 8.61667 20 10V14H18Z"
                                  fill="#1C1B1F"
                                />
                              </svg>
                            </Button>
                            <h6 className="m-0 themeBlue fw-bold">
                              Merry's Permissions
                            </h6>
                          </div>
                        </div>
                      </Col>
                      <Col lg="6" className="my-2">
                        <div
                          className="p-3 rounded shadow d-flex align-items-center justify-content-between"
                          style={{ background: "#FEFFE9" }}
                        >
                          <div className="left d-flex align-items-center gap-10">
                            <span className="icn">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="26"
                                height="30"
                                viewBox="0 0 26 30"
                                fill="none"
                              >
                                <path
                                  d="M12 30C11.175 30 10.4688 29.7063 9.88125 29.1188C9.29375 28.5312 9 27.825 9 27H15C15 27.825 14.7063 28.5312 14.1188 29.1188C13.5313 29.7063 12.825 30 12 30ZM0 25.5V22.5H3V12C3 9.925 3.625 8.08125 4.875 6.46875C6.125 4.85625 7.75 3.8 9.75 3.3V2.25C9.75 1.625 9.96875 1.09375 10.4062 0.65625C10.8438 0.21875 11.375 0 12 0C12.625 0 13.1562 0.21875 13.5938 0.65625C14.0312 1.09375 14.25 1.625 14.25 2.25V3.3C14.525 3.375 14.7938 3.45625 15.0563 3.54375C15.3188 3.63125 15.575 3.7375 15.825 3.8625L13.4625 6.1875C13.2125 6.1375 12.975 6.09375 12.75 6.05625C12.525 6.01875 12.275 6 12 6C10.35 6 8.9375 6.5875 7.7625 7.7625C6.5875 8.9375 6 10.35 6 12V22.5H18V19.35L21 16.35V22.5H24V25.5H0ZM12 16.5V11.8875L20.2875 3.6375C20.5125 3.4125 20.7625 3.25 21.0375 3.15C21.3125 3.05 21.5875 3 21.8625 3C22.1625 3 22.45 3.05625 22.725 3.16875C23 3.28125 23.25 3.45 23.475 3.675L24.8625 5.0625C25.0625 5.2875 25.2188 5.5375 25.3313 5.8125C25.4438 6.0875 25.5 6.3625 25.5 6.6375C25.5 6.9125 25.45 7.19375 25.35 7.48125C25.25 7.76875 25.0875 8.025 24.8625 8.25L16.6125 16.5H12ZM14.25 14.25H15.675L20.2125 9.675L19.5375 8.9625L18.825 8.2875L14.25 12.825V14.25ZM19.5375 8.9625L18.825 8.2875L20.2125 9.675L19.5375 8.9625Z"
                                  fill="#1C1B1F"
                                />
                              </svg>
                            </span>
                            <div className="content">
                              <h6 className="m-0">Assigned Clients</h6>
                              <p className="m-0">Status Update Notifications</p>
                            </div>
                          </div>
                          <div className="right">
                            <div className=" d-flex align-items-center">
                              <p className="m-0 text-muted mx-1">Off</p>
                              <Switch
                                uncheckedIcon={""}
                                checkedIcon={""}
                                onHandleColor={"#A3DF00"}
                                offHandleColor={"#A3DF00"}
                                onColor={"#E6E6E6"}
                                offColor={"#E6E6E6"}
                                height={18}
                                width={40}
                                onChange={handleChange}
                                checked={checked}
                              />
                              <p className="m-0 text-muted mx-1">On</p>
                            </div>
                          </div>
                        </div>
                        <div className="py-2">
                          <Row>
                            <Col lg="12" className="my-2">
                              <h6 className="m-0 themeBlue fw-bold pb-2">
                                Designation
                              </h6>
                              <div className="py-2">
                                <Row className="py-2">
                                  <Col md="3" className="my-2">
                                    <h6 className="m-0 themeBlue fw-bold pb-2">
                                      Veteran
                                    </h6>
                                  </Col>
                                  <Col md="9" className="my-2">
                                    <ul className="list-unstyled ps-0 mb-0 d-flex align-items-center gap-10 flex-wrap">
                                      <li className="d-flex align-items-center gap-10">
                                        {" "}
                                        <input
                                          type="checkbox"
                                          className="form-check"
                                          style={{ height: 20, width: 20 }}
                                        />
                                        <p className="m-0">All Permission</p>
                                      </li>
                                      <li className="d-flex align-items-center gap-10">
                                        {" "}
                                        <input
                                          type="checkbox"
                                          className="form-check"
                                          style={{ height: 20, width: 20 }}
                                        />
                                        <p className="m-0">Sharing Videos</p>
                                      </li>
                                      <li className="d-flex align-items-center gap-10">
                                        {" "}
                                        <input
                                          type="checkbox"
                                          className="form-check"
                                          style={{ height: 20, width: 20 }}
                                        />
                                        <p className="m-0">share resources</p>
                                      </li>
                                      <li className="d-flex align-items-center gap-10">
                                        {" "}
                                        <input
                                          type="checkbox"
                                          className="form-check"
                                          style={{ height: 20, width: 20 }}
                                        />
                                        <p className="m-0">
                                          Access Chatter Lessons
                                        </p>
                                      </li>
                                      <li className="d-flex align-items-center gap-10">
                                        {" "}
                                        <input
                                          type="checkbox"
                                          className="form-check"
                                          style={{ height: 20, width: 20 }}
                                        />
                                        <p className="m-0">Email</p>
                                      </li>
                                    </ul>
                                  </Col>
                                </Row>
                                <Row className="py-2">
                                  <Col md="3" className="my-2">
                                    <h6 className="m-0 themeBlue fw-bold pb-2">
                                      Apprentice
                                    </h6>
                                  </Col>
                                  <Col md="9" className="my-2">
                                    <ul className="list-unstyled ps-0 mb-0 d-flex align-items-center gap-10 flex-wrap">
                                      <li className="d-flex align-items-center gap-10">
                                        {" "}
                                        <input
                                          type="checkbox"
                                          className="form-check"
                                          style={{ height: 20, width: 20 }}
                                        />
                                        <p className="m-0">share resources</p>
                                      </li>
                                      <li className="d-flex align-items-center gap-10">
                                        {" "}
                                        <input
                                          type="checkbox"
                                          className="form-check"
                                          style={{ height: 20, width: 20 }}
                                        />
                                        <p className="m-0">Send Reports</p>
                                      </li>
                                      <li className="d-flex align-items-center gap-10">
                                        {" "}
                                        <input
                                          type="checkbox"
                                          className="form-check"
                                          style={{ height: 20, width: 20 }}
                                        />
                                        <p className="m-0">
                                          supervisor signature
                                        </p>
                                      </li>
                                      <li className="d-flex align-items-center gap-10">
                                        {" "}
                                        <input
                                          type="checkbox"
                                          className="form-check"
                                          style={{ height: 20, width: 20 }}
                                        />
                                        <p className="m-0">
                                          import/export caseload lists
                                        </p>
                                      </li>
                                    </ul>
                                  </Col>
                                </Row>
                              </div>
                            </Col>
                          </Row>
                        </div>
                      </Col> */}
                    </>
                  ) : (
                    <>
                      {/* <Col lg="12" className="my-3">
                        <div className="pageHead d-flex align-items-center justify-content-between flex-wrap gap-10 border-bottom pb-2">
                          <div className="left d-flex align-items-center gap-10">
                            <Link
                              className="themeBlue fw-sbold back-to-btn"
                              to="/manage-team/therapist-profiles/"
                            >
                              <svg width="20" height="14" viewBox="0 0 20 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M6 12L0 6L6 0L7.425 1.4L2.825 6L7.425 10.6L6 12ZM18 14V10C18 9.16667 17.7083 8.45833 17.125 7.875C16.5417 7.29167 15.8333 7 15 7H8.825L12.425 10.6L11 12L5 6L11 0L12.425 1.4L8.825 5H15C16.3833 5 17.5625 5.4875 18.5375 6.4625C19.5125 7.4375 20 8.61667 20 10V14H18Z" fill="#1C1B1F" />
                              </svg> Back
                            </Link>
                            <h5 className="m-0 text-capitalize fw-bold themeBlue">
                              {therapistData?.firstname} Details
                            </h5>
                          </div>
                          <div className="right">
                            <Form>
                              <div className="d-flex align-itmes-center gap-10">
                              </div>
                            </Form>
                          </div>
                        </div>
                      </Col>
                      <Col lg="12" className="my-3">
                        <div className=" py-2">
                          <div className="top d-flex align-items-center justify-content-between gap-10 flex-wrap">
                            <div className="left d-flex align-items-center gap-10">
                              <div
                                className="igmWrp flex-shrink-0 d-flex themeBlue fw-sbold align-items-center justify-content-center rounded-circle"
                                style={{
                                  height: 35,
                                  width: 35,
                                  background: "#DBDBDB",
                                }}
                              >
                                {getNameInitials(therapistData)}
                              </div>
                              <div className="content">
                                <h6 className="m-0 themeBlue fw-sbold">
                                  {userFullName(therapistData)}
                                </h6>
                              </div>
                            </div>
                          </div>
                          <ul className="list-unstyled ps-0 mb-0 d-flex gap-10 py-3 flex-wrap therapist-boxes-ui therapist-boxes-ui1">
                            <li className="">
                              <div className="cardCstm p-2 bg-white shadow h-100 rounded">
                                <h6 className="m-0 themeBlue fw-bold">
                                  {" "}
                                  <span
                                    className=""
                                    style={{ color: "#3DA0AA" }}
                                  >
                                    ■
                                  </span>{" "}
                                  Created at
                                </h6>
                                <p className="m-0 forteen themeBlue pt-2">
                                  {moment(therapistData?.createdAt).format(
                                    "DD/MM/YYYY, hh:mm A"
                                  )}
                                </p>
                              </div>
                            </li>
                            <li className="">
                              <div className="cardCstm p-2 bg-white shadow h-100 rounded">
                                <h6 className="m-0 themeBlue fw-bold">
                                  {" "}
                                  <span
                                    className=""
                                    style={{ color: "#3DA0AA" }}
                                  >
                                    ■
                                  </span>{" "}
                                  Phone
                                </h6>
                                <p className="m-0 forteen themeBlue pt-2">
                                  {therapistData?.phone}
                                </p>
                              </div>
                            </li>
                            <li className="calender-link-fit-cont">
                              <div className="cardCstm p-2 bg-white shadow h-100 rounded">
                                <h6 className="m-0 themeBlue fw-bold">
                                  {" "}
                                  <span
                                    className=""
                                    style={{ color: "#3DA0AA" }}
                                  >
                                    ■
                                  </span>{" "}
                                  Calendar link
                                </h6>
                                <div className="d-flex align-items-center calender-link-fit-link">
                                  <p className="m-0 forteen themeBlue pt-2 me-2 elipse-calendly">
                                    {
                                      therapistData?.therapist_detail
                                        ?.calendly_link
                                    }
                                  </p>
                                  <div>
                                    <ClipboardCopyBtn
                                      textToCopy={
                                        therapistData?.therapist_detail
                                          ?.calendly_link
                                      }
                                    />
                                  </div>
                                </div>
                              </div>
                            </li>
                          </ul>
                          <ul className="list-unstyled ps-0 mb-0 d-flex gap-10 flex-wrap therapist-boxes-ui therapist-boxes-ui2">
                            <li className="">
                              <div className="cardCstm p-2 bg-white shadow h-100 rounded">
                                <h6 className="m-0 themeBlue fw-bold">
                                  {" "}
                                  <span
                                    className=""
                                    style={{ color: "#3DA0AA" }}
                                  >
                                    ■
                                  </span>{" "}
                                  Therapist Email
                                </h6>
                                <div className="d-flex align-items-center">
                                  <p className="m-0 forteen themeBlue pt-2">
                                    {therapistData?.email}
                                  </p>
                                </div>
                              </div>
                            </li>
                            <li className="">
                              <div className="cardCstm p-2 bg-white shadow h-100 rounded">
                                <h6 className="m-0 themeBlue fw-bold">
                                  {" "}
                                  <span
                                    className=""
                                    style={{ color: "#3DA0AA" }}
                                  >
                                    ■
                                  </span>{" "}
                                  Assigned Discipline Type
                                </h6>
                                <p className="m-0 forteen themeBlue pt-2">
                                  {therapistData?.therapist_discipline?.name}
                                </p>
                              </div>
                            </li>
                            <li className="">
                              <div className="cardCstm p-2 bg-white shadow h-100 rounded">
                                <h6 className="m-0 themeBlue fw-bold">
                                  {" "}
                                  <span
                                    className=""
                                    style={{ color: "#3DA0AA" }}
                                  >
                                    ■
                                  </span>{" "}
                                  Assigned Client Category
                                </h6>
                                <div className="d-flex align-items-center">
                                  <p className="m-0 forteen themeBlue pt-2 text-capitalize">
                                    {
                                      therapistData?.assigned_client_category?.split(",")?.map((item, index) => {

                                        return item && <span
                                          style={{ marginRight: "10px", padding: "5px 10px", borderRadius: "5px" }}
                                          className={`greenStatus status text-capitalize`}
                                        >
                                          {
                                            CLIENT_TYPE[item]
                                          }
                                        </span>
                                      })
                                    }
                                  </p>
                                </div>
                              </div>
                            </li>
                            <li className="">
                              <div className="cardCstm p-2 bg-white shadow h-100 rounded">
                                <h6 className="m-0 themeBlue fw-bold">
                                  {" "}
                                  <span
                                    className=""
                                    style={{ color: "#3DA0AA" }}
                                  >
                                    ■
                                  </span>{" "}
                                  Designation
                                </h6>
                                <p className="m-0 forteen themeBlue pt-2">
                                  {therapistData?.therapist_designation?.title}
                                </p>
                              </div>
                            </li>
                          </ul>
                          <ul className="list-unstyled ps-0 mb-0 d-flex gap-10 py-3 flex-wrap therapist-boxes-ui therapist-boxes-ui3">
                            <li className="">
                              <div className="cardCstm p-2 bg-white shadow h-100 rounded">
                                <h6 className="m-0 themeBlue fw-bold">
                                  {" "}
                                  <span
                                    className=""
                                    style={{ color: "#3DA0AA" }}
                                  >
                                    ■
                                  </span>{" "}
                                  Therapist Type
                                </h6>
                                <div className="d-flex align-items-center">
                                  <p className="m-0 forteen themeBlue pt-2 text-capitalize">
                                    {therapistData?.type}
                                  </p>
                                </div>
                              </div>
                            </li>
                            <li className="">
                              <div className="cardCstm p-2 bg-white shadow h-100 rounded">
                                <h6 className="m-0 themeBlue fw-bold">
                                  {" "}
                                  <span
                                    className=""
                                    style={{ color: "#3DA0AA" }}
                                  >
                                    ■
                                  </span>{" "}
                                  Registration Number
                                </h6>
                                <div className="d-flex align-items-center">
                                  <p className="m-0 forteen themeBlue pt-2">
                                    {therapistData?.registration_number}
                                  </p>
                                </div>
                              </div>
                            </li>
                            <li className="">
                              <div className="cardCstm p-2 bg-white shadow h-100 rounded">
                                <h6 className="m-0 themeBlue fw-bold">
                                  {" "}
                                  <span
                                    className=""
                                    style={{ color: "#3DA0AA" }}
                                  >
                                    ■
                                  </span>{" "}
                                  Insurance Policy Number
                                </h6>
                                <div className="d-flex align-items-center">
                                  <p className="m-0 forteen themeBlue pt-2">
                                    {therapistData?.insurance_policy_number}
                                  </p>
                                </div>
                              </div>
                            </li>
                            <li className="">
                              <div className="d-flex gap-2 align-items-center cardCstm p-2 bg-white shadow h-100 rounded">
                                <div className="d-flex align-items-center">
                                  <p className="m-0 forteen themeBlue pt-2">
                                    <input disabled={isFetching || updateCollaboratorLoading} checked={therapistData?.colloborators_on_notes} onClick={(e) => handleUpdateCollaborator(e)} style={{ height: "20px", width: "20px" }} type="checkbox" />
                                  </p>
                                </div>
                                <h6 className="m-0 themeBlue fw-bold">
                                  Collaborator
                                </h6>
                              </div>
                            </li>
                          </ul>
                          <div className="py-3">
                            <h6 className="m-0 themeBlue fw-sbold text-capitalize">
                              About {therapistData?.firstname}
                            </h6>
                            <p className="m-0 forteen themeBlue">
                              {therapistData?.self_description}
                            </p>
                          </div>
                        </div>
                        <div className="py-2">
                          <h6 className="m-0 pb-2 fw-sbold themeBlue">
                            Therapist's signature
                          </h6>
                          <div className="cardCstm p-3 bg-white shadow rounded">
                            <Row>
                              <Col>
                                <div className="imgWrp">
                                  {therapistData?.therapist_detail
                                    ?.signature_img && (
                                      <img
                                        src={
                                          therapistData?.therapist_detail
                                            ?.signature_img
                                        }
                                        alt=""
                                        className="img-fluid object-fit-contain"
                                        style={{ height: 100 }}
                                      />
                                    )}
                                </div>
                                {therapistData?.therapist_detail
                                  ?.email_signature && (
                                    <p className="m-0 fw-sbold themeBlue nu-multiline-text">
                                      {parseHtmlHelper(therapistData?.therapist_detail?.email_signature)}
                                    </p>
                                  )}
                              </Col>
                            </Row>
                          </div>
                        </div>
                        <div className="py-3">
                          <h6 className="m-0 themeBlue fw-sbold text-capitalize mb-2">
                            Assigned Schools
                          </h6>
                          <div className="cardCstm p-3 bg-white shadow rounded">
                            {
                              assignedSchools?.length > 0 ?
                                assignedSchools?.map((item) => {
                                  return (
                                    <Row className="mb-4">
                                      <Col xs="6" md='4'>
                                        <label className="themeBlue">School Divisions</label><br />
                                        <p className="m-0 forteen themeBlue pt-2 text-capitalize">{item?.divisionTitle}</p>
                                      </Col>
                                      <Col xs="6" md='4'>
                                        <label className="themeBlue">Schools</label><br />
                                        {
                                          item?.schools?.map((list) => (
                                            <p className="m-0 forteen themeBlue pt-2 text-capitalize">{list?.name}</p>
                                          ))
                                        }
                                      </Col>
                                    </Row>
                                  )
                                })
                                : (
                                  <NoRecordFoundV2 />
                                )}
                          </div>
                        </div>
                      </Col> */}
                    </>
                  )}
                </Row>
              )}
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default TherapistDetail;
